import {
  IconPlayerPause,
  IconPlayerPlay,
  IconVolume,
} from "@tabler/icons-react";
import { Duration } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useFocusVisible } from "react-aria";
import { Button as AriaButton } from "react-aria-components";
import { AudioPlayerTrigger } from "./audioPlayerTrigger.js";

export interface AudioPlayerTimestampPlayButtonProps {
  /** The source this fragment belongs to. */
  sourceKey: string;
  /** The start time of this fragment. */
  time: number;
  /** The start time of the next fragment. Used to determine whether this fragment is playing. */
  nextTime: number | undefined;
}

export const AudioPlayerTimestampPlayButton: FC<
  AudioPlayerTimestampPlayButtonProps
> = (props) => {
  const { isFocusVisible } = useFocusVisible();

  // What action we should show based on the last time the button was focused
  const [focusAction, setFocusAction] = useState<"play" | "pause" | undefined>(
    undefined,
  );
  const focusVisibleAction = isFocusVisible ? focusAction : undefined;

  // What action we should show based on the last time the button was hovered
  const [hoverAction, setHoverAction] = useState<"play" | "pause" | undefined>(
    undefined,
  );

  // Whether the button is currently hovered or keyboard-focused. When it is, we
  // show the play/pause control; otherwise we show the speaker icon (while playing).
  const isInteracting =
    hoverAction !== undefined || focusVisibleAction !== undefined;

  const formattedTime = useMemo(
    () => Duration.fromMillis(props.time * 1000).toFormat("h:mm:ss"),
    [props.time],
  );

  return (
    <AudioPlayerTrigger
      sourceKey={props.sourceKey}
      time={props.time}
      nextTime={props.nextTime}
    >
      {({ isPlaying, onPress }) => {
        // What action we should show if the button is neither focused nor hovered (mostly for accessibility, because the button has no opacity in this case)
        const defaultAction = isPlaying ? "pause" : "play";

        // What action we should actually show
        const action = hoverAction ?? focusVisibleAction ?? defaultAction;

        const playOrPauseLabel =
          action === "play" ? `Play from ${formattedTime}` : "Pause";
        const PlayOrPauseIcon =
          action === "play" ? IconPlayerPlay : IconPlayerPause;

        return (
          <AriaButton
            className="group/timestamp flex items-center gap-1"
            aria-label={playOrPauseLabel}
            data-is-playing={isPlaying}
            // When focusing or hovering, capture the playing state so we don't change the action due to the audio proceeding to the next fragment
            onFocus={() => setFocusAction(isPlaying ? "pause" : "play")}
            onBlur={() => setFocusAction(undefined)}
            onHoverStart={() => setHoverAction(isPlaying ? "pause" : "play")}
            onHoverEnd={() => setHoverAction(undefined)}
            onPress={(event) => {
              onPress(event, action);

              // When pressing the button, we do want to update the action
              setFocusAction(handledAction(action));
              setHoverAction(handledAction(action));
            }}
          >
            <div className="size-4 opacity-0 transition-opacity group-hover/timestamp:opacity-100 group-focus-visible/timestamp:opacity-100 group-data-[is-playing=true]/timestamp:opacity-100">
              {isPlaying && !isInteracting ? (
                <IconVolume className="size-full text-indigo-500" />
              ) : (
                <PlayOrPauseIcon className="size-full text-muted-foreground" />
              )}
            </div>

            <span className="font-mono text-xs text-muted-foreground select-none">
              {formattedTime}
            </span>
          </AriaButton>
        );
      }}
    </AudioPlayerTrigger>
  );
};

/** Creates a reducer / state update that flips the current action, if it matches the action that was performed. */
function handledAction(action: "play" | "pause") {
  const reciprocalAction = action === "play" ? "pause" : "play";

  return (prevAction: "play" | "pause" | undefined) => {
    return prevAction === action ? reciprocalAction : prevAction;
  };
}
